import type { OperationsNode } from '../types/operations.types'
import { isTeamNode } from './nodeTypes'
import { findNodeById } from './findNodeById'
import { findNodeParent } from './findNodeParent'
import { resolveLandedOrder } from './resolveLandedOrder'

export interface PendingMove {
  /** Composite node id of the dragged team. */
  activeId: string
  /** The row it was dropped on — a team, or a division to append into. */
  overId: string
  /** Land BELOW `overId` rather than above it — the pointer's slot. */
  after: boolean
}

/**
 * The tree as it will look once the server confirms a dropped team.
 *
 * Returns a NEW tree with the team actually relocated, rather than a per-parent
 * ordering the renderer patches in. That distinction matters: an ordering map
 * can only shuffle a parent's EXISTING children, so a team arriving from
 * another division was absent from the destination's `children`, got filtered
 * out, and stayed painted in its old division until the refetch landed.
 *
 * Dropping onto a division row appends — the only possible gesture when that
 * division is empty or collapsed.
 *
 * The team's own `scheduleId` is rewritten so its row id stays consistent with
 * its new parent while the move is in flight.
 */
export const applyPendingMove = (
  nodes: OperationsNode[],
  move: PendingMove | null
): OperationsNode[] => {
  if (!move) {
    return nodes
  }

  const dragged = findNodeById(nodes, move.activeId)
  const over = findNodeById(nodes, move.overId)
  if (!dragged || !over || !isTeamNode(dragged)) {
    return nodes
  }

  const source = findNodeParent(nodes, move.activeId)
  const destination = isTeamNode(over)
    ? findNodeParent(nodes, move.overId)
    : over
  if (!source || !destination || destination.type !== 'division') {
    return nodes
  }

  const moved: OperationsNode = {
    ...dragged,
    scheduleId: destination.scheduleId,
  }

  // The settled order, from the one shared rule — see `resolveLandedOrder`.
  const landedIds = resolveLandedOrder(
    destination.children.map((child) => child.id),
    move.activeId,
    move.overId,
    move.after
  )

  const rebuild = (node: OperationsNode): OperationsNode => {
    // A team has no children to rebuild, and its `children` is typed as the
    // empty tuple — spreading a widened array into it would not type-check.
    if (isTeamNode(node)) {
      return node
    }

    // Remove from wherever it currently sits.
    const remaining = node.children.filter(
      (child) => child.id !== move.activeId
    )

    if (node.id !== destination.id) {
      return { ...node, children: remaining.map(rebuild) }
    }

    const byId = new Map<string, OperationsNode>([
      ...remaining.map((child): [string, OperationsNode] => [child.id, child]),
      [move.activeId, moved],
    ])
    const children = landedIds
      .map((id) => byId.get(id))
      .filter((child): child is OperationsNode => Boolean(child))

    return { ...node, children: children.map(rebuild) }
  }

  return nodes.map(rebuild)
}
